import { DOCUMENT } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { SellerService } from "src/app/core";
import { DataTableColumn } from "src/app/shared/ui/data-table/data-table.component";

@Component({
  selector: "app-expiring-points",
  templateUrl: "./expiring-points.component.html",
  styleUrls: ["./expiring-points.component.scss"],
})
export class ExpiringPointsComponent implements OnInit, AfterViewInit {
  @Output() redeemClicked = new EventEmitter<any>();

  @ViewChild("expiryDateCell") expiryDateCell!: TemplateRef<any>;
  @ViewChild("pointsCell") pointsCell!: TemplateRef<any>;
  @ViewChild("daysRemainingCell") daysRemainingCell!: TemplateRef<any>;

  isExpanded = false;
  expiringSections: any[] = [];
  pointsExpData: any;
  expiringColumns: DataTableColumn[] = [
    { field: "expiryDate", header: "Expiry Date", align: "center" },
    { field: "points", header: "Points", align: "center" },
    { field: "daysRemaining", header: "Days Remaining", align: "center" },
  ];

  bucketConfig: { [key: string]: any } = {
    EXPIRY_BUCKET_7: {
      title: "Expiring in 7 days",
      subtitle: "Urgent - Use these points soon!",
      headerColor: "text-pink",
      labelColor: "label-pink",
      dangerLimit: 7,
    },
    EXPIRY_BUCKET_14: {
      title: "Expiring in 14 days",
      subtitle: "Act soon to avoid losing points",
      headerColor: "text-info",
      labelColor: "label--info",
      dangerLimit: 14,
    },
    EXPIRY_BUCKET_30: {
      title: "Expiring in 30 days",
      subtitle: "Plan to use these points this month",
      headerColor: "text-warning",
      labelColor: "label--warning",
      dangerLimit: 30,
    },
    EXPIRY_BUCKET_30_PLUS: {
      title: "Expiring Later",
      subtitle: "No immediate action needed",
      headerColor: "text-success",
      labelColor: "label--success",
      dangerLimit: 999,
    },
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private sellerService: SellerService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    //this.loadCiscoStyles("client-cisco.css");
    this.pointsExpData = this.getPointsExpiryData();
  }

  ngAfterViewInit() {
    this.expiringColumns[0].cellTemplate = this.expiryDateCell;
    this.expiringColumns[1].cellTemplate = this.pointsCell;
    this.expiringColumns[2].cellTemplate = this.daysRemainingCell;
    this.cdr.detectChanges();
  }

  getPointsExpiryData() {
    this.sellerService.getpointsExpiryTrackerData().subscribe((res) => {
      if (res) {
        this.buildSections(res);
      }
    });
  }

  buildSections(res: any) {
    const api = res;
    this.expiringSections = [];

    const isSevenDayBucketPresent = !!api["EXPIRY_BUCKET_7"];

    Object.keys(api).forEach((bucketKey) => {
      const cfg = this.bucketConfig[bucketKey];
      if (!cfg) return;

      const bucket = api[bucketKey];
      let dynamicSubtitle = cfg.subtitle;

      // Apply urgent subtitle ONLY if EXPIRY_BUCKET_7 exists
      if (bucketKey === "EXPIRY_BUCKET_7" && isSevenDayBucketPresent) {
        const points = bucket.rewardsThatWillBeExpired || 0;
        const formatted = new Intl.NumberFormat("en-US").format(points);

        dynamicSubtitle = `
          <span class="text-danger text-bold">Urgent</span>
          — Redeem at least ${formatted} points now to avoid expiration.
        `;
      }

      this.expiringSections.push(
        this.mapBucket(
          bucket,
          cfg.title,
          dynamicSubtitle,
          cfg.headerColor,
          cfg.labelColor,
          cfg.dangerLimit
        )
      );
    });
  }

  mapBucket(
    bucket: any,
    title: string,
    subtitle: string,
    headerColor: string,
    labelColor: string,
    dangerLimit: number
  ) {
    const records = bucket.pointsExpiryTrackerIndividualRecordDTOSet || [];

    const items = records.map((x: any) => ({
      earnedDate: new Date(x.earnedOn),
      expiryDate: new Date(x.expiresOn),
      points: x.rewardsThatWillBeExpired,
      daysRemaining: this.calculateDaysRemaining(x.expiresOn),
      labelColor,
    }));

    return {
      title,
      subtitle,
      headerColor,
      labelColor,
      dangerLimit,
      totalPoints: bucket.rewardsThatWillBeExpired,
      transactions: bucket.numberOfTransactions,
      expanded: false,
      items,
    };
  }

  calculateDaysRemaining(expiresOn: string): number {
    const today = new Date();
    const exp = new Date(expiresOn);
    const diff = exp.getTime() - today.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  loadCiscoStyles(styleName: string) {
    const head = this.document.getElementsByTagName("head")[0];
    const styleLink = this.document.createElement("link");
    styleLink.rel = "stylesheet";
    styleLink.classList.add("cisco_styles");
    styleLink.href = styleName;
    head.appendChild(styleLink);
  }

  toggleSection(index: number) {
    this.expiringSections[index].expanded =
      !this.expiringSections[index].expanded;
  }

  onRedeemClick() {
    this.redeemClicked.emit();
  }
}
